import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Crown, Sparkles, ArrowRight, Plane, MapPin, FileCheck, MessageCircle, ClipboardCheck, Fingerprint, CreditCard, FileText, Landmark, CalendarDays, BadgeCheck, ShieldCheck, MessagesSquare } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { trackBeginCheckout } from "@/lib/analytics";
import { CURRENCY, IMAGES } from "@/lib/constants";
import { Section, Overline } from "@/components/Primitives";
import { Seo } from "@/components/Seo";
import { AccompanimentSection } from "@/components/AccompanimentSection";
import { GoogleReviewsSection } from "@/components/GoogleReviewsSection";
import { breadcrumbSchema } from "@/components/Breadcrumbs";
import { TrustBadge } from "@/components/TrustBadge";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const BANNER = {
  canada: "https://static.prod-images.emergentagent.com/jobs/1e641627-b4a5-48d2-9cf6-4476cf306fa6/images/78cdd8607e2b3ba6d2ea1f2d05b23831a46e531e3b7554c7f25a423cdc8377dc.jpeg",
  australie: "https://static.prod-images.emergentagent.com/jobs/1e641627-b4a5-48d2-9cf6-4476cf306fa6/images/950811d4d3d76e5a17a77e91a6b8d17b57fdfe5b6fbc4afc6a71d54e50157909.jpeg",
};

const S = "https://static.prod-images.emergentagent.com/jobs/1e641627-b4a5-48d2-9cf6-4476cf306fa6/images/";
const VISA_CA = S + "44fa6a5c38f0bdd6f0847bea423a4ce8534d5787a13b807a301014d289a40e8f.jpeg";
const VISA_AU = S + "ff10db167cf3913a090bbd93baab9e714e75718d5e4728b0b5f2f7c6e4f5237a.jpeg";
const PREP = S + "776d9630d47dc852f5aae73544de11976a6cf8edf218bfbdeb123f4484c62452.jpeg";
const INSTALL_CA = S + "1620a5d64fa8ff8d3a71b7eb30143bb339a0adc2d003544a7ebcac865497597b.jpeg";
const INSTALL_AU = S + "f574d58709de085bdab4ffec5d866d4cfa8c1747d3a10c4f3615a7c78ba1620b.jpeg";
const PREMIUM_CA = S + "8b841c1e26271cdc4cfeef26fd89fc2feeb2e05d7c698ff084a12a351b9cab20.jpeg";
const PREMIUM_AU = S + "d4e0c276f5ce2c2ef78f2bb1de398cf73589e7781c00874dde221fc2d6294feb.jpeg";
const CONSULT = S + "86bc93eeeaa14e48f8da5c0423286c1d792878202709f35eca60c67c0e83c0b9.jpeg";
const VERIF = S + "65c42332edf07d6b9a15eb98a97c551dc118c9b9db02ac6101a9baa2fef9222c.jpeg";
const BIOM = S + "50603bbd8f44e408a4e4908e3cbaee129c899c9ad1db0b2397c5b78b0cd22be5.jpeg";
const NAS = S + "1732107be5bdb19b5865a809a94037f7bcef0a5e3e58f75484587c4c34555763.jpeg";
const TFN = S + "f34791e1612cadcb176f42a6bf8ef93ba02343f705770b1828fc82f289b939aa.jpeg";
const CV_IMG = S + "7bcdb562ab3d7cfe38a1ea29329b27aeab1b3db49b02db98c1fb0d14f9506e05.jpeg";
const BANK = S + "dd81c52f1f5e287b7d406076dcca5bce632ae42fc4064ecef0b67d207070e9b9.jpeg";

const IMG = {
  "visa-canada": VISA_CA, "visa-australie": VISA_AU, "prep-canada": PREP, "prep-australie": PREP,
  "install-canada": INSTALL_CA, "install-australie": INSTALL_AU, "premium-canada": PREMIUM_CA, "premium-australie": PREMIUM_AU,
  "svc-consultation-ca": CONSULT, "svc-consultation-au": CONSULT, "svc-verification-ca": VERIF, "svc-verification-au": VERIF,
  "svc-biometrie-ca": BIOM, "svc-biometrie-au": BIOM, "svc-nas-ca": NAS, "svc-tfn-au": TFN,
  "svc-cv-ca": CV_IMG, "svc-cv-au": CV_IMG, "svc-banque-ca": BANK, "svc-banque-au": BANK,
};

const iconFor = (id) => {
  if (id.startsWith("visa")) return FileCheck;
  if (id.startsWith("prep")) return Plane;
  if (id.startsWith("install")) return MapPin;
  if (id.startsWith("premium")) return Crown;
  if (id.includes("consultation")) return MessageCircle;
  if (id.includes("verification")) return ClipboardCheck;
  if (id.includes("biometrie")) return Fingerprint;
  if (id.includes("nas") || id.includes("tfn")) return CreditCard;
  if (id.includes("cv")) return FileText;
  if (id.includes("banque")) return Landmark;
  return Check;
};

// Classes statiques (Tailwind JIT) par accent de couleur
const ACCENT = {
  red:    { title: "text-[#C8102E]", check: "text-[#C8102E]", badge: "bg-[#C8102E]", icon: "bg-[#C8102E]", btn: "bg-[#C8102E] hover:bg-[#a50d26]" },
  navy:   { title: "text-navy",      check: "text-navy",      badge: "bg-navy",      icon: "bg-navy",      btn: "bg-navy hover:bg-navy-light" },
  green:  { title: "text-[#2E7D32]", check: "text-[#2E7D32]", badge: "bg-[#2E7D32]", icon: "bg-[#2E7D32]", btn: "bg-[#2E7D32] hover:bg-[#25692a]" },
  purple: { title: "text-[#6A1B9A]", check: "text-[#6A1B9A]", badge: "bg-[#6A1B9A]", icon: "bg-[#6A1B9A]", btn: "bg-[#6A1B9A] hover:bg-[#571580]" },
  pink:   { title: "text-[#C2185B]", check: "text-[#C2185B]", badge: "bg-[#C2185B]", icon: "bg-[#C2185B]", btn: "bg-[#C2185B] hover:bg-[#a01549]" },
  teal:   { title: "text-[#00796B]", check: "text-[#00796B]", badge: "bg-[#00796B]", icon: "bg-[#00796B]", btn: "bg-[#00796B] hover:bg-[#005f54]" },
};

const packAccentKey = (pack) => {
  if (pack.id.startsWith("prep")) return "green";
  if (pack.id.startsWith("install")) return "purple";
  return pack.country === "canada" ? "red" : "navy"; // visa
};
const SERVICE_ACCENTS = ["navy", "green", "red", "navy", "pink", "teal"];

const THEME = {
  canada: { title: "CANADA", script: "text-[#C8102E]", flag: "🇨🇦", subtitle: "votre visa et votre installation au Canada.", specialist: "Spécialistes visas Canada" },
  australie: { title: "AUSTRALIE", script: "text-gold", flag: "🇦🇺", subtitle: "votre visa et votre installation en Australie.", specialist: "Spécialistes des visas Australie" },
};

const COUNTRIES = [
  { id: "canada", label: "Canada", flag: "🇨🇦", img: IMAGES.canada },
  { id: "australie", label: "Australie", flag: "🇦🇺", img: IMAGES.australia },
];

export default function Packs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCountry = ["canada", "australie"].includes((searchParams.get("pays") || "").toLowerCase())
    ? searchParams.get("pays").toLowerCase()
    : null;
  const [packs, setPacks] = useState([]);
  const [country, setCountry] = useState(initialCountry);
  const [loadingId, setLoadingId] = useState(null);
  const [pendingPack, setPendingPack] = useState(null);
  const [consent, setConsent] = useState(false);
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  useEffect(() => { api.get("/packs").then((r) => setPacks(r.data)).catch(() => {}); }, []);

  const selectCountry = (id) => {
    setCountry(id);
    setSearchParams({ pays: id }, { replace: true });
  };

  const reserve = (pack) => {
    if (!user) { toast.info(t("packs.loginNeeded")); navigate("/connexion"); return; }
    if (user.role === "admin") { toast.info("Utilisez un compte client pour réserver."); return; }
    setConsent(false);
    setPendingPack(pack);
  };

  const confirmReserve = async () => {
    if (!consent) { toast.error(t("packs.consentRequired")); return; }
    const pack = pendingPack;
    setLoadingId(pack.id);
    try {
      const { data } = await api.post("/bookings", { pack_id: pack.id, origin_url: window.location.origin });
      setPendingPack(null);
      if (data.url) { trackBeginCheckout(pack); window.location.href = data.url; }
      else { toast.success(data.message || "Dossier créé."); navigate("/dashboard/suivi"); setLoadingId(null); }
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); setLoadingId(null); }
  };

  const countryPacks = packs.filter((p) => p.country === country && p.kind === "pack");
  const countryServices = packs.filter((p) => p.country === country && p.kind === "service");
  const th = country ? THEME[country] : null;

  return (
    <div data-testid="packs-page">
      <Seo
        title="Nos packs & services d'accompagnement — Canada & Australie | Work & Travel Expert"
        description="Choisissez votre pays puis votre formule : Pack Visa, Préparation au départ, Installation à l'arrivée, Premium et services à la carte. Paiement sécurisé et suivi en ligne."
        keywords="pack visa canada, pack visa australie, services immigration, NAS, TFN, CV, accompagnement départ"
        path="/nos-packs"
        schema={[
          breadcrumbSchema([{ name: "Nos packs", to: "/nos-packs" }]),
          {
            "@context": "https://schema.org",
            "@type": "OfferCatalog",
            name: "Packs et services d'accompagnement Work & Travel Expert",
            url: "https://worktravelexpert.net/nos-packs",
            inLanguage: "fr-FR",
            provider: { "@type": "Organization", name: "Work & Travel Expert", url: "https://worktravelexpert.net" },
            itemListElement: packs.map((p) => ({
              "@type": "Offer",
              name: p.name,
              description: p.tagline,
              price: p.price,
              priceCurrency: (p.currency || "eur").toUpperCase(),
              availability: "https://schema.org/InStock",
              url: "https://worktravelexpert.net/nos-packs",
              category: p.country === "canada" ? "Immigration Canada" : "Immigration Australie",
            })),
          },
        ]}
      />

      {/* SÉLECTEUR DE PAYS */}
      <div className="relative bg-navy overflow-hidden">
        <div className="pointer-events-none absolute -top-40 -right-24 w-[34rem] h-[34rem] rounded-full bg-gold/15 blur-[120px]" />
        <Section className="relative z-10 py-14 text-center">
          <Overline>Nos formules</Overline>
          <h1 className="mt-4 text-4xl sm:text-5xl font-medium text-white">Choisissez votre destination</h1>
          <div className="gold-line mx-auto mt-6" />
          <div className="mt-9 grid sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
            {COUNTRIES.map((c) => (
              <button key={c.id} data-testid={`country-btn-${c.id}`} onClick={() => selectCountry(c.id)}
                className={`group relative h-36 sm:h-44 rounded-2xl overflow-hidden ring-2 transition-all duration-300 ${country === c.id ? "ring-gold scale-[1.02]" : "ring-white/15 hover:ring-gold/60"}`}>
                <img src={c.img} alt={c.label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className={`absolute inset-0 transition-colors ${country === c.id ? "bg-navy/45" : "bg-navy/65 group-hover:bg-navy/55"}`} />
                <div className="relative z-10 h-full flex flex-col items-center justify-center gap-1.5">
                  <span className="text-5xl">{c.flag}</span>
                  <span className="font-heading text-3xl text-white">{c.label}</span>
                  {country === c.id && <span className="text-xs uppercase tracking-widest text-gold font-semibold">✓ Sélectionné</span>}
                </div>
              </button>
            ))}
          </div>
        </Section>
      </div>

      {!country ? (
        <Section className="py-20 text-center">
          <p data-testid="packs-empty" className="text-muted-foreground">👆 Sélectionnez le Canada ou l'Australie pour afficher les packs et services.</p>
        </Section>
      ) : (
        <>
          {/* EN-TÊTE PAYS IMMERSIF */}
          <div className="relative h-[340px] sm:h-[440px] overflow-hidden" data-testid={`country-hero-${country}`}>
            <img src={BANNER[country]} alt={th.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-white/85 via-white/40 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
            <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 h-full flex flex-col justify-center">
              <motion.h2 initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}
                className={`font-heading font-bold leading-none tracking-tight text-6xl sm:text-8xl ${th.script}`}>{th.title}</motion.h2>
              <p className={`mt-2 font-heading italic text-2xl sm:text-4xl ${th.script}`} style={{ fontFamily: "cursive" }}>Votre aventure commence ici !</p>
              <p className="mt-4 max-w-md text-navy font-semibold text-base sm:text-xl leading-snug">Accompagnement complet pour {th.subtitle}</p>
            </div>
          </div>

          {/* NOS PACKS */}
          <Section className="py-14">
            <GoogleReviewsSection variant="compact" className="mb-12 max-w-5xl mx-auto" />
            <div className="flex items-center justify-center gap-4">
              <span className="text-2xl">{th.flag}</span>
              <h2 className="text-center font-heading text-3xl sm:text-4xl font-bold text-navy uppercase tracking-wide">Nos packs</h2>
              <span className="text-2xl">{th.flag}</span>
            </div>
            <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="packs-grid">
              {countryPacks.map((pack, i) => {
                const Icon = iconFor(pack.id);
                if (pack.featured) {
                  return (
                    <motion.div key={pack.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }}
                      data-testid={`pack-card-${pack.id}`}
                      className="relative flex flex-col rounded-2xl overflow-hidden bg-[#0d1117] ring-2 ring-gold shadow-2xl">
                      <div className="absolute top-0 right-0 z-10 bg-gold text-navy text-[10px] font-bold px-5 py-1.5 rounded-bl-2xl flex items-center gap-1"><Crown size={12} /> PACK SPÉCIAL PREMIUM</div>
                      <div className="relative h-28 overflow-hidden">
                        <img src={IMG[pack.id]} alt={pack.name} loading="lazy" className="w-full h-full object-cover opacity-70" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1117] to-transparent" />
                        <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 h-12 w-12 rounded-full bg-gold flex items-center justify-center text-navy shadow-lg"><Crown size={24} /></div>
                      </div>
                      <div className="flex flex-col flex-1 p-6 pt-8 text-center">
                        <h3 className="font-heading text-xl text-white leading-tight">{pack.name}</h3>
                        <div className="mt-3 mx-auto bg-gold text-navy font-heading text-3xl font-bold px-5 py-1.5 rounded-md">{CURRENCY(pack.price)}</div>
                        <p className="text-gold text-xs font-semibold mt-2 uppercase tracking-wide">{pack.note}</p>
                        <ul className="mt-4 space-y-1.5 flex-1 text-left">
                          {pack.features.map((f) => (
                            <li key={f} className="flex items-start gap-2 text-[11px] text-white/85"><Check size={13} className="text-gold shrink-0 mt-0.5" /> {f}</li>
                          ))}
                        </ul>
                        <button data-testid={`reserve-btn-${pack.id}`} disabled={loadingId === pack.id} onClick={() => reserve(pack)}
                          className="mt-5 w-full py-3 rounded-lg font-bold text-sm flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover text-navy transition-colors disabled:opacity-60">
                          <CalendarDays size={15} /> {loadingId === pack.id ? "Redirection…" : "CHOISIR CE PACK"}
                        </button>
                      </div>
                    </motion.div>
                  );
                }
                const a = ACCENT[packAccentKey(pack)];
                return (
                  <motion.div key={pack.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }}
                    data-testid={`pack-card-${pack.id}`}
                    className="relative flex flex-col rounded-2xl overflow-hidden border border-border bg-white hover:shadow-lg transition-all">
                    <div className="relative h-32 overflow-hidden">
                      <img src={IMG[pack.id]} alt={pack.name} loading="lazy" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-navy/15" />
                      <div className={`absolute -bottom-5 left-1/2 -translate-x-1/2 h-11 w-11 rounded-full ${a.icon} flex items-center justify-center text-white shadow-lg`}><Icon size={22} /></div>
                    </div>
                    <div className="flex flex-col flex-1 p-6 pt-8 text-center">
                      <h3 className={`font-heading text-lg leading-tight font-bold uppercase ${a.title}`}>{pack.name}</h3>
                      <div className={`mt-3 mx-auto ${a.badge} text-white font-heading text-2xl px-5 py-1.5 rounded-md`}>{CURRENCY(pack.price)}</div>
                      <ul className="mt-4 space-y-2 flex-1 text-left">
                        {pack.features.map((f) => (
                          <li key={f} className="flex items-start gap-2 text-xs text-slate-700"><Check size={14} className={`${a.check} shrink-0 mt-0.5`} /> {f}</li>
                        ))}
                      </ul>
                      <button data-testid={`reserve-btn-${pack.id}`} disabled={loadingId === pack.id} onClick={() => reserve(pack)}
                        className={`mt-5 w-full py-3 rounded-lg font-bold text-sm flex items-center justify-center gap-2 ${a.btn} text-white transition-colors disabled:opacity-60`}>
                        <CalendarDays size={15} /> {loadingId === pack.id ? "Redirection…" : "CHOISIR CE PACK"}
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </Section>

          {/* NOS SERVICES À LA CARTE */}
          <div className="bg-[#F8F9FA]">
            <Section className="py-14">
              <h2 className="text-center font-heading text-2xl sm:text-3xl font-bold text-navy uppercase tracking-wide">— Nos services à la carte —</h2>
              <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="services-grid">
                {countryServices.map((svc, i) => {
                  const Icon = iconFor(svc.id);
                  const a = ACCENT[SERVICE_ACCENTS[i % SERVICE_ACCENTS.length]];
                  return (
                    <motion.div key={svc.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
                      data-testid={`service-card-${svc.id}`}
                      className="flex flex-col rounded-2xl overflow-hidden border border-border bg-white hover:shadow-lg transition-all">
                      <div className="relative h-28 overflow-hidden">
                        <img src={IMG[svc.id]} alt={svc.name} loading="lazy" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-navy/25" />
                        <div className={`absolute -bottom-4 left-1/2 -translate-x-1/2 h-10 w-10 rounded-full ${a.icon} flex items-center justify-center text-white shadow-lg`}><Icon size={19} /></div>
                      </div>
                      <div className="flex flex-col flex-1 p-5 pt-7 text-center">
                        <h3 className={`font-heading text-sm font-bold uppercase leading-tight ${a.title}`}>{svc.name}</h3>
                        <ul className="mt-3 space-y-1 flex-1 text-left">
                          {svc.features.map((f) => (
                            <li key={f} className="flex items-start gap-1.5 text-[11px] text-slate-700"><Check size={12} className={`${a.check} shrink-0 mt-0.5`} /> {f}</li>
                          ))}
                        </ul>
                        <div className={`mt-3 font-heading text-2xl ${a.title}`}>{CURRENCY(svc.price)}</div>
                        <button data-testid={`reserve-btn-${svc.id}`} disabled={loadingId === svc.id} onClick={() => reserve(svc)}
                          className={`mt-3 w-full py-2.5 rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 ${a.btn} text-white transition-colors disabled:opacity-60`}>
                          <CalendarDays size={13} /> {loadingId === svc.id ? "…" : "CHOISIR CE SERVICE"}
                        </button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </Section>
          </div>

          {/* WTE AI PREMIUM */}
          {packs.find((p) => p.id === "wte-ai-premium") && (
            <Section className="pb-14">
              <div data-testid="ai-premium-card" className="grid items-center gap-8 rounded-2xl border border-gold/30 bg-navy p-8 text-white sm:p-10 lg:grid-cols-[1.2fr_0.8fr]">
                <div>
                  <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-[11px] uppercase tracking-[0.18em] text-gold">
                    <Sparkles size={13} /> WTE AI Premium
                  </span>
                  <h2 className="mt-4 font-heading text-2xl sm:text-3xl">Votre assistant IA approfondi, 24h/24</h2>
                  <p className="mt-4 text-sm leading-relaxed text-white/80">
                    Analyse de votre profil, voies recommandées, rétroplanning indicatif et questions illimitées.
                    Les informations restent sourcées et datées, et l'IA ne garantit jamais l'obtention d'un visa.
                  </p>
                </div>
                <div className="text-left lg:text-right">
                  <p className="font-heading text-4xl text-gold">10 €<span className="ml-1 text-sm text-white/60">/mois</span></p>
                  <button
                    data-testid="ai-premium-buy-btn"
                    onClick={() => reserve(packs.find((p) => p.id === "wte-ai-premium"))}
                    className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-3.5 text-xs font-bold uppercase tracking-[0.1em] text-navy transition-colors hover:bg-gold-hover"
                  >
                    Activer Premium <ArrowRight size={15} />
                  </button>
                  <p className="mt-3 text-[11px] text-white/50">Activation automatique après paiement · 30 jours</p>
                </div>
              </div>
            </Section>
          )}

          {/* VOUS ÊTES ACCOMPAGNÉ À CHAQUE ÉTAPE */}
          <AccompanimentSection />

          {/* BARRE DE CONFIANCE */}
          <div className="bg-navy">
            <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
              {[
                { icon: BadgeCheck, txt: th.specialist },
                { icon: MapPin, txt: "Accompagnement à chaque étape" },
                { icon: MessagesSquare, txt: "Service client 7j/7 sur WhatsApp" },
                { icon: ShieldCheck, txt: "Paiement sécurisé — en 2 fois possible" },
              ].map((it, k) => (
                <div key={k} className="flex flex-col items-center gap-2">
                  <it.icon size={26} className="text-gold" />
                  <span className="text-white/85 text-xs sm:text-sm font-medium leading-snug">{it.txt}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* DIALOGUE DE CONSENTEMENT (flux inchangé) */}
      <Dialog open={!!pendingPack} onOpenChange={(o) => { if (!o) setPendingPack(null); }}>
        <DialogContent className="max-w-lg rounded-xl max-h-[90vh] overflow-y-auto" data-testid="reserve-consent-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-navy">{t("packs.consentTitle")}</DialogTitle>
          </DialogHeader>
          {pendingPack && <p className="text-sm text-muted-foreground -mt-2">{pendingPack.name} · {CURRENCY(pendingPack.price)}</p>}
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <TrustBadge theme="light" link={false} />
            <span className="inline-flex items-center gap-1.5 rounded-full border border-navy/15 bg-[#F8F9FA] px-3.5 py-1.5 text-[11px] sm:text-xs font-medium text-navy">
              <ShieldCheck size={14} className="text-gold shrink-0" /> Paiement sécurisé par Stripe
            </span>
          </div>
          <label className="flex items-start gap-3 cursor-pointer text-sm text-navy bg-[#F8F9FA] border border-border p-4 mt-2 rounded-lg">
            <input type="checkbox" data-testid="reserve-consent-checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 h-4 w-4 accent-gold shrink-0" />
            <span className="leading-relaxed">{t("packs.consent")}</span>
          </label>
          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <button data-testid="reserve-cancel-btn" onClick={() => setPendingPack(null)} className="flex-1 border border-border text-navy py-3 rounded-lg hover:bg-[#F8F9FA] transition-colors">{t("packs.cancel")}</button>
            <button data-testid="reserve-confirm-btn" disabled={!consent || loadingId} onClick={confirmReserve}
              className="flex-1 bg-gold hover:bg-gold-hover text-navy py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              {loadingId ? "Redirection…" : <>{t("packs.confirm")} <ArrowRight size={16} /></>}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
